import React from 'react';
import { Crown, ShoppingBag, Scroll, Trophy, Wallet, LogOut, Terminal, Swords, Backpack, Twitter } from 'lucide-react';
import { AnimatedIcon } from './ui/AnimatedIcon';
import { NavigationCard } from './ui/NavigationCard';
import type { User } from '../types/user';

interface HomePageProps {
  onNavigate: (path: string) => void;
  user: User | null;
  onLogout: () => void;
}

export function HomePage({ onNavigate, user, onLogout }: HomePageProps) {
  const sections = [
    {
      title: "Kingdom Arcade",
      description: "Challenge yourself in royal games of skill and wit",
      icon: Crown,
      path: "/arcade",
      gradient: "from-yellow-600 to-amber-500",
      animatedIcon: Crown
    },
    {
      title: "Mystical Marketplace",
      description: "Acquire legendary creatures and enchanted artifacts",
      icon: ShoppingBag,
      path: "/training",
      gradient: "from-purple-600 to-pink-500",
      animatedIcon: ShoppingBag
    },
    {
      title: "Royal Treasury",
      description: "View your collection of pets and items",
      icon: Backpack,
      path: "/inventory",
      gradient: "from-amber-600 to-yellow-500",
      animatedIcon: Backpack
    },
    {
      title: "Royal Library",
      description: "Study ancient scrolls and learn the kingdom's lore",
      icon: Scroll,
      path: "/library",
      gradient: "from-blue-600 to-cyan-500",
      animatedIcon: Scroll
    },
    {
      title: "Hall of Champions",
      description: "View the greatest warriors and their achievements",
      icon: Trophy,
      path: "/champions",
      gradient: "from-indigo-600 to-violet-500",
      animatedIcon: Trophy
    },
    {
      title: "Solstice Terminal",
      description: "Consult with the ancient AI oracle of the realm",
      icon: Terminal,
      path: "/terminal",
      gradient: "from-cyan-600 to-blue-500",
      animatedIcon: Terminal
    }
  ];

  return (
    <div className="min-h-screen bg-amber-900 text-amber-100 p-8 bg-[url('https://images.unsplash.com/photo-1578662996442-48f60103fc96?auto=format&fit=crop&q=80')] bg-cover bg-center bg-blend-overlay">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-12">
          <div className="flex items-center gap-4">
            <a 
              href="https://x.com/Solstice_PvP" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              <Twitter className="w-6 h-6" />
            </a>
          </div>
          <div className="text-center flex-1">
            <h1 className="text-5xl font-bold font-serif mb-4 bg-gradient-to-r from-yellow-500 to-amber-500 text-transparent bg-clip-text">
              Welcome to Solstice PvP
            </h1>
            <p className="text-xl text-amber-200 font-serif">Choose your path, brave adventurer</p>
          </div>
          {user && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3 bg-gray-900/50 px-4 py-2 rounded-lg border border-amber-600/20">
                <div className="text-right">
                  <div className="text-yellow-500 font-serif">{user.username}</div>
                  <div className="flex items-center gap-1 text-sm text-amber-200">
                    <Wallet className="w-3 h-3" />
                    <span className="font-mono">{user.points} coins</span>
                  </div>
                </div>
              </div>
              <button
                onClick={onLogout}
                className="p-2 text-amber-200 hover:text-amber-100 transition-colors"
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        {/* Navigation Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sections.map((section) => (
            <div key={section.title} className="relative">
              <NavigationCard {...section} onClick={() => onNavigate(section.path)} />
              <AnimatedIcon icon={section.animatedIcon} />
            </div>
          ))}
        </div>

        {/* Battle Section */}
        <div className="mt-8 relative">
          <div className="bg-gradient-to-br from-red-600 to-orange-500 p-0.5 rounded-xl cursor-pointer transform hover:scale-105 transition-all"
               onClick={() => onNavigate('/battle')}>
            <div className="bg-gray-900 p-8 rounded-lg text-center">
              <div className="flex justify-center items-center gap-4 mb-6">
                <Swords className="w-12 h-12 text-red-500" />
              </div>
              <h2 className="text-3xl font-bold font-serif mb-4 bg-gradient-to-r from-red-500 to-orange-500 text-transparent bg-clip-text">
                Battle!
              </h2>
              <p className="text-xl text-amber-200 font-serif mb-6">
                Challenge other players to epic battles using your pets and items!
              </p>
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-600 to-orange-500 rounded-lg text-white hover:from-red-500 hover:to-orange-400">
                <Swords className="w-5 h-5" />
                <span className="font-serif">Enter Arena</span>
              </div>
            </div>
            <AnimatedIcon icon={Swords} className="text-red-500" />
          </div>
        </div>
      </div>
    </div>
  );
}